import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security, useOktaAuth } from '@okta/okta-react';
import { JSX, PropsWithChildren, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Route, Switch, useHistory } from 'react-router-dom';
import {
  mainPotsPortalUrl,
  OKTA_CALLBACK_PATH,
  OKTA_LOGIN_PATH,
  OKTA_SOURCE_PARAM,
  parseOktaLoginSource,
  setOktaLoginSource,
  oktaAuth,
} from '../../features/okta/okta.config';
import Spinner from '../Other/Spinner/Spinner';

const OktaLoading = (): JSX.Element => (
  <>
    <Helmet>
      <title>Authenticating...</title>
    </Helmet>
    <Spinner />
  </>
);

const OktaLogin = (): JSX.Element => {
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();
  useEffect(() => {
    if (window.location.origin === mainPotsPortalUrl?.origin) {
      setOktaLoginSource(parseOktaLoginSource(new URLSearchParams(window.location.search).get(OKTA_SOURCE_PARAM)));
      oktaAuth.signInWithRedirect();
    } else if (mainPotsPortalUrl) {
      window.location.replace(
        new URL(
          `${OKTA_LOGIN_PATH}?${OKTA_SOURCE_PARAM}=${encodeURIComponent(window.location.origin)}`,
          mainPotsPortalUrl.origin
        ).toString()
      );
    } else {
      history.replace('/authentication/login');
    }
  }, [oktaAuth, history]);
  return <OktaLoading />;
};

const OktaCallback = (): JSX.Element => <LoginCallback loadingElement={<OktaLoading />} />;

/**
 * Component responsibilities:
 * - Supplies the useOktaAuth context to all child components.
 * - Defines routes for Okta-related paths (login and callback).
 * - Restores the original URL after login/logout redirects.
 *
 * Should be placed as high in the component tree as possible, but still beneath the Router.
 */
const OktaProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const history = useHistory();
  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string | undefined) =>
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Switch>
        <Route exact path={OKTA_LOGIN_PATH} component={OktaLogin} />
        <Route exact path={OKTA_CALLBACK_PATH} component={OktaCallback} />
        <Route render={() => children} />
      </Switch>
    </Security>
  );
};

export default OktaProvider;
